<script setup lang='ts'>
import useCheckout from '@/apps/checkout/composables/useCheckout';
import { computed, onMounted, ref, toValue, useTemplateRef, watch, watchEffect } from 'vue';
import { useStripePayment } from '../composables/useStripePayment';
import {i18n} from "@/libs/wordpress.facade";
import BacsPayment from './payment/BacsPayment.vue';
import StripePayment from './payment/StripePayment.vue';
import { useCheckoutCustomField } from '../composables/useWooCheckout';
import useGlobalAlert from '../composables/useGlobalAlert';
import { pushAddPaymentInfo } from '../composables/useGtagManager';
import AxeptaPayment from './payment/AxeptaPayment.vue';

const {wooCheckout, step, wooCart} = useCheckout();
const {getPaymentData, handleStripePaymentComplete} = useStripePayment();

wooCheckout.refreshCheckout();

const termsField = useCheckoutCustomField('itlp/terms_and_conditions', 'checkbox', false);
const privacyField = useCheckoutCustomField('itlp/privacy', 'checkbox', false);
const newsletterField = useCheckoutCustomField('itlp/newsletter_subscription', 'checkbox', false);

async function pay(){
	if(wooCart.requesting.value === 'paying' || wooCart.requesting.value === 'redirecting') return;
	try{
		wooCart.requesting.value = 'paying';
		let paymentData = undefined;

		const additionalFields: Record<string, string|number|undefined> = {};
		if(termsField.enabled.value) additionalFields[termsField.key] = termsField.value.value ? 'yes' : 0;
		if(privacyField.enabled.value) additionalFields[privacyField.key] = privacyField.value.value ? 'yes' : 0;
		if(newsletterField.enabled.value) additionalFields[newsletterField.key] = newsletterField.value.value ? 'yes' : 0;

		// let server validating?
		/*if(!termsField.validate() || !privacyField.validate() || !newsletterField.validate()){
			return;
		}*/

		if(wooCheckout.selectedPaymentMethodId.value === 'stripe'){
			try{
				paymentData = await getPaymentData()
			}catch(e){
				// stop, stripe will show a error message
				return;
			}
		}

		let shippingAddress = wooCart.cart.value!.shipping_address;
		// use billing address as shipping if no real shipping is needed like for italdeposito
		if(!wooCart.showShippingAddressStep.value){
			shippingAddress = wooCart.cart.value!.billing_address;
		}

		await wooCheckout.placeOrder({
			billing_address: wooCart.cart.value!.billing_address,
			shipping_address: shippingAddress,
			payment_method: wooCheckout.selectedPaymentMethodId.value as string,
			payment_data: paymentData,
			additional_fields: additionalFields
		});

		const stripeHandled = await handleStripePaymentComplete();
		if(!stripeHandled) return; // something failed

		if(wooCheckout.checkout.value?.payment_result?.payment_status == 'success'){
			if(wooCheckout.checkout.value?.payment_result?.redirect_url){
				window.location.href = wooCheckout.checkout.value.payment_result.redirect_url;
				wooCart.requesting.value = 'redirecting';
			}
			// else ?? redirect to cart?
		}else{
			console.error('checkut response', wooCheckout.checkout.value, wooCheckout.postOrderBodyError.value);
		}
	}finally{
		if(wooCart.requesting.value == 'paying') wooCart.requesting.value = false;
	}
}

function back(){
	if(!wooCart.showShippingAddressStep.value){
		if(wooCart.showShippingMethodStep.value) step.value -= 2;
		else step.value -= 3;
	}else step.value--;
}

const checkoutErrorMessage = computed(() => {
	if(!wooCheckout.postOrderBodyError.value) return undefined;

	if(wooCheckout.postOrderBodyError.value.data?.params?.billing_address){
		// redirect to billing address step??
		return wooCheckout.postOrderBodyError.value.data.params.billing_address;
	}
	if(wooCheckout.postOrderBodyError.value.data?.params?.shipping_address){
		// redirect to shipping address step??
		return wooCheckout.postOrderBodyError.value.data.params.shipping_address;
	}
	if(wooCheckout.postOrderBodyError.value.data?.params?.additional_fields){
		return wooCheckout.postOrderBodyError.value.data.params.additional_fields;
	}

	return wooCheckout.postOrderBodyError.value.message;
});

const {addAlert} = useGlobalAlert();

watch(checkoutErrorMessage, () => {
	if(checkoutErrorMessage.value) addAlert(checkoutErrorMessage.value, 5000);
});

watch(wooCheckout.selectedPaymentMethodId, (paymentMethodId) => {
	if(paymentMethodId && wooCart.cart.value?.payment_methods && !wooCart.cart.value.payment_methods.includes(paymentMethodId)){
		wooCheckout.selectedPaymentMethodId.value = wooCart.cart.value.payment_methods[0];
		return;
	}
	pushAddPaymentInfo(paymentMethodId);
});

</script>

<template lang="pug">
.itlp-checkout-payment-content
	fieldset.itlp-checkout-payment-methods
		legend.screen-reader-text {{i18n.__('Metodo di pagamento', 'italpreziosi')}}
		BacsPayment
		AxeptaPayment
		StripePayment
	.itlp-checkout-payment-form
		.itlp-checkout-payment-form
			.itlp-checkout-payment-form-input.itlp-checkout-payment-form-input-terms(v-if='termsField.enabled.value' :class="{'itlp-input-has-error': termsField.hasError.value}")
				label.itlp-checkout-payment-form-checkbox(:class="{'itlp-input-has-error': termsField.hasError.value}")
					input(type='checkbox' :name="termsField.key" v-model="termsField.value.value" :required="termsField.required.value")
					span(v-html="termsField.label.value")
			.itlp-checkout-payment-form-input.itlp-checkout-payment-form-input-privacy(v-if='privacyField.enabled.value' :class="{'itlp-input-has-error': privacyField.hasError.value}")
				label.itlp-checkout-payment-form-checkbox(:class="{'itlp-input-has-error': privacyField.hasError.value}")
					input(type='checkbox' :name="privacyField.key" v-model="privacyField.value.value" :required="privacyField.required.value")
					span(v-html="privacyField.label.value")
			.itlp-checkout-payment-form-input.itlp-checkout-payment-form-input-newsletter(v-if='newsletterField.enabled.value' :class="{'itlp-input-has-error': newsletterField.hasError.value}")
				label.itlp-checkout-payment-form-checkbox(:class="{'itlp-input-has-error': newsletterField.hasError.value}")
					input(type='checkbox' :name="newsletterField.key" v-model="newsletterField.value.value" :required="newsletterField.required.value")
					span(v-html="newsletterField.label.value")
.itlp-checkout-payment-footer
	button(@click='back()') {{i18n.__('Indietro', 'italpreziosi')}}
	button(:disabled="!wooCheckout.checkout.value || wooCart.requesting.value === 'paying' || wooCart.requesting.value === 'redirecting'" @click='pay()') {{i18n.__('Effettua ordine', 'italpreziosi')}}
</template>

<style>
.itlp-checkout-payment-footer button:disabled{
	cursor: not-allowed;
	opacity: 0.6;
}
</style>